import { useEffect, useRef } from "react";

import { useToast } from "@/hooks/use-toast";

export interface ParBreachNotificationInput {
  level_id: string;
  product_id: string | null;
  product_name: string;
  location_id: string | null;
  location_name: string | null;
  on_hand: number;
  reorder_point: number | null;
  deficit: number;
  coverage_days: number | null;
  severity: "critical" | "warning";
  updated_at: number | null;
}

type NotificationVariant = "default" | "destructive" | "success";

interface ParBreachNotificationOptions {
  disabled?: boolean;
  maxNotifications?: number;
  formatNotification?: (
    breach: ParBreachNotificationInput,
  ) => {
    title: string;
    description?: string;
    variant?: NotificationVariant;
    duration?: number;
  };
}

const toNotificationKey = (breach: ParBreachNotificationInput): string => {
  if (breach.level_id) {
    return breach.level_id;
  }
  if (breach.product_id) {
    return `${breach.product_id}:${breach.location_id ?? "unassigned"}`;
  }
  return `${breach.product_name}:${breach.location_id ?? "unassigned"}`;
};

export const useParBreachNotifications = (
  breaches: ParBreachNotificationInput[],
  options: ParBreachNotificationOptions = {},
): void => {
  const { toast } = useToast();
  const seenRef = useRef<Map<string, number | null>>(new Map());
  const {
    disabled = false,
    maxNotifications = 3,
    formatNotification,
  } = options;

  useEffect(() => {
    if (disabled) {
      return;
    }

    const seen = seenRef.current;
    const nextSeen = new Map<string, number | null>();
    const freshBreaches: ParBreachNotificationInput[] = [];

    for (const breach of breaches) {
      const key = toNotificationKey(breach);
      const updatedAt = breach.updated_at ?? null;
      const lastSeen = seen.get(key);
      nextSeen.set(key, updatedAt ?? lastSeen ?? null);

      if (breach.severity !== "critical") {
        continue;
      }

      const isFirstObservation = lastSeen === undefined;
      const isNewTimestamp =
        updatedAt !== null && (lastSeen === null || (lastSeen ?? -Infinity) < updatedAt);

      if (isFirstObservation || isNewTimestamp) {
        freshBreaches.push(breach);
      }
    }

    if (freshBreaches.length > 0) {
      const limit = maxNotifications;
      freshBreaches
        .slice(0, limit)
        .forEach((breach) => {
          const payload = formatNotification
            ? formatNotification(breach)
            : {
                title: `Critical stock alert: ${breach.product_name}`,
                description: breach.location_name
                  ? `${breach.location_name} has ${breach.on_hand} on hand (par ${breach.reorder_point ?? 0}).`
                  : `${breach.product_name} is under par with ${breach.on_hand} on hand (par ${breach.reorder_point ?? 0}).`,
                variant: "destructive" as NotificationVariant,
              };

          toast({
            title: payload.title,
            description: payload.description,
            variant: payload.variant ?? "destructive",
            duration: payload.duration ?? 8000,
          });
        });
    }

    seenRef.current = nextSeen;
  }, [breaches, disabled, formatNotification, maxNotifications, toast]);
};

export interface KpiGuardrailNotificationInput {
  id: string;
  title: string;
  description?: string;
  severity: 'warning' | 'critical';
  breachedAt?: number | null;
  duration?: number;
}

interface KpiGuardrailNotificationOptions {
  disabled?: boolean;
  maxNotifications?: number;
}

export const useKpiGuardrailNotifications = (
  inputs: KpiGuardrailNotificationInput[],
  options: KpiGuardrailNotificationOptions = {},
): void => {
  const { toast } = useToast();
  const seenRef = useRef<Map<string, { severity: 'warning' | 'critical'; breachedAt: number | null }>>(new Map());

  useEffect(() => {
    if (options.disabled) {
      return;
    }

    const seen = seenRef.current;
    const nextSeen = new Map<string, { severity: 'warning' | 'critical'; breachedAt: number | null }>();
    const breaches = inputs.filter((entry) => entry.severity === 'warning' || entry.severity === 'critical');

    if (breaches.length === 0) {
      seenRef.current = nextSeen;
      return;
    }

    const fresh: KpiGuardrailNotificationInput[] = [];

    breaches.forEach((entry) => {
      const previous = seen.get(entry.id);
      const breachedAt = entry.breachedAt ?? null;
      nextSeen.set(entry.id, { severity: entry.severity, breachedAt });

      const severityChanged = !previous || previous.severity !== entry.severity;
      const timestampChanged = breachedAt !== null && (!previous || previous.breachedAt !== breachedAt);

      if (severityChanged || timestampChanged) {
        fresh.push(entry);
      }
    });

    const limit = options.maxNotifications ?? fresh.length;
    fresh
      .slice(0, limit)
      .forEach((entry) => {
        toast({
          title: entry.title,
          description: entry.description,
          variant: entry.severity === 'critical' ? 'destructive' : 'default',
          duration: entry.duration ?? 7000,
        });
      });

    seenRef.current = nextSeen;
  }, [inputs, options.disabled, options.maxNotifications, toast]);
};
